import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { postData, getData } from '@/services/api.service';
import { end_points } from '@/services/endpoints';
import { toast } from 'react-toastify';
import { routes } from '@/utils/routes/routes';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  addDays,
  format,
  parseISO,
  differenceInDays,
  isBefore,
} from 'date-fns';

const TableBooking = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    booking_date: '',
    booking_time: '',
    peoples: '',
    notes: ''
  });
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [holidays, setHolidays] = useState<string[]>([]);
  const [bookingTimeSlots, setBookingTimeSlots] = useState<any[]>([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Generate holiday dates array from holidays list
  // Expands date ranges (from_date to to_date) into individual dates
  const generateHolidayDates = (holidayArray: any[]) => {
    if (!holidayArray || !Array.isArray(holidayArray)) {
      return;
    }

    const allDates: string[] = [];

    holidayArray.forEach(({ from_date, to_date }) => {
      if (!from_date || !to_date) return;
      
      try {
        const start = parseISO(from_date);
        const end = parseISO(to_date);
        
        // Normalize dates to start of day for accurate comparison
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        
        // Handle edge case: if to_date is before from_date, swap them
        let actualStart = start;
        let actualEnd = end;
        
        if (isBefore(end, start)) {
          // to_date is before from_date - swap them
          actualStart = end;
          actualEnd = start;
          console.warn('Holiday date range reversed: to_date is before from_date. Swapping dates.', { from_date, to_date });
        }
        
        const daysDiff = differenceInDays(actualEnd, actualStart);

        // Include all dates from from_date to to_date (inclusive)
        for (let i = 0; i <= daysDiff; i++) {
          const date = addDays(actualStart, i);
          const formatted = format(date, 'yyyy-MM-dd');
          allDates.push(formatted);
        }
      } catch (error) {
        console.error('Error parsing holiday date range:', { from_date, to_date }, error);
      }
    });

    // Remove duplicates and sort
    const uniqueDates = Array.from(new Set(allDates)).sort();
    setHolidays(uniqueDates);
  };

  // Get today's date (reset time to start of day)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if a date is a holiday (for Date objects)
  const isHoliday = (date: Date) => {
    if (!date) return false;
    // Normalize date to start of day for accurate comparison
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    const formatted = format(normalizedDate, 'yyyy-MM-dd');
    return holidays.includes(formatted);
  };

  // Check if a date string (yyyy-MM-dd format) is a holiday
  const isHolidayDate = (dateString: string) => {
    if (!dateString) return false;
    return holidays.includes(dateString);
  };

  // Check if a date is selectable (not before today and not a holiday)
  const isDateSelectable = (date: Date) => {
    if (!date) return false;
    // Normalize date to start of day for accurate comparison
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return !isBefore(normalizedDate, today) && !isHoliday(normalizedDate);
  };

  // Get next valid date (not a holiday and not before today)
  const getNextValidDate = (startDate: Date) => {
    let nextDate = new Date(startDate);
    nextDate.setHours(0, 0, 0, 0);
    
    // If start date is before today, start from today
    if (isBefore(nextDate, today)) {
      nextDate = new Date(today);
    }
    
    // Find next date that is not a holiday
    let attempts = 0;
    const maxAttempts = 365; // Prevent infinite loop
    while (isHoliday(nextDate) && attempts < maxAttempts) {
      nextDate = addDays(nextDate, 1);
      attempts++;
    }
    
    return nextDate;
  };

  // Fetch holidays on component mount
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const response = await getData(end_points.tableBooking.url);
        if (response?.data?.data?.holidays) {
          generateHolidayDates(response.data.data.holidays);
        }
      } catch (error: any) {
        console.error('Error fetching holidays:', error);
        // Don't show error toast as this is a background operation
      }
    };

    fetchHolidays();
  }, []);

  // Fetch time slots when a date is selected
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!selectedDate) {
        setBookingTimeSlots([]);
        setFormData(prev => ({ ...prev, booking_time: '' }));
        return;
      }

      // Format date as yyyy-MM-dd for API
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');

      setLoadingTimeSlots(true);
      try {
        const response = await getData(end_points.tableBooking.url, {
          booking_date: formattedDate
        });

        if (response?.data?.data?.booking_timeslots && Array.isArray(response.data.data.booking_timeslots)) {
          setBookingTimeSlots(response.data.data.booking_timeslots);
        } else {
          setBookingTimeSlots([]);
        }
        // Reset selected time when date changes
        setFormData(prev => ({ ...prev, booking_time: '' }));
      } catch (error: any) {
        console.error('Error fetching time slots:', error);
        toast.error('Failed to load available time slots');
        setBookingTimeSlots([]);
      } finally {
        setLoadingTimeSlots(false);
      }
    };

    fetchTimeSlots();
  }, [selectedDate]);

  // Generate time slots from 9 AM to 9 PM (1-hour intervals)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 21; hour++) {
      const time12 = hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`;
      const time24 = `${hour.toString().padStart(2, '0')}:00`;
      slots.push({ label: time12, value: time24 });
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Handle date change from DatePicker
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      const formattedDate = format(date, 'yyyy-MM-dd');
      setFormData(prev => ({
        ...prev,
        booking_date: formattedDate
      }));
      // Clear error for booking_date
      if (errors.booking_date) {
        setErrors((prev: any) => {
          const newErrors = { ...prev };
          delete newErrors.booking_date;
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        booking_date: ''
      }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^[0-9+\-\s()]+$/.test(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid mobile number';
    }

    if (!formData.booking_date || !selectedDate) {
      newErrors.booking_date = 'Booking date is required';
    } else {
      // Check if date is a holiday (DatePicker should prevent this, but validate anyway)
      if (isHoliday(selectedDate)) {
        newErrors.booking_date = 'This date is a holiday and cannot be selected for booking';
      }
      // DatePicker already prevents past dates, but validate anyway
      const normalizedDate = new Date(selectedDate);
      normalizedDate.setHours(0, 0, 0, 0);
      if (isBefore(normalizedDate, today)) {
        newErrors.booking_date = 'Booking date cannot be in the past';
      }
    }

    if (!formData.booking_time) {
      newErrors.booking_time = 'Booking time is required';
    }

    if (!formData.peoples) {
      newErrors.peoples = 'Number of people is required';
    } else if (parseInt(formData.peoples) < 1) {
      newErrors.peoples = 'Number of people must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile: formData.mobile.trim(),
        booking_date: formData.booking_date,
        booking_time: formData.booking_time,
        peoples: parseInt(formData.peoples),
        notes: formData.notes.trim() || null
      };

      const response = await postData(end_points.bookTable.url, payload);

      if (response?.data?.success) {
        toast.success(response?.data?.message || 'Booking request submitted successfully!');
        // Reset form
        setFormData({
          name: '',
          email: '',
          mobile: '',
          booking_date: '',
          booking_time: '',
          peoples: '',
          notes: ''
        });
        setSelectedDate(null);
        setBookingTimeSlots([]);
        // Navigate to home or show success message
        setTimeout(() => {
          navigate(routes.home);
        }, 2000);
      } else {
        toast.error(response?.data?.message || 'Failed to submit booking request');
      }
    } catch (error: any) {
      console.error('Booking error:', error);
      toast.error(error?.response?.data?.message || 'An error occurred while submitting your booking request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h3 className="card-title mb-0">Table Booking</h3>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="mb-3">
                  <label htmlFor="mobile" className="form-label">
                    Mobile Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                  />
                  {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="booking_date" className="form-label">
                      Booking Date <span className="text-danger">*</span>
                    </label>
                    <DatePicker
                      selected={selectedDate}
                      onChange={handleDateChange}
                      filterDate={isDateSelectable}
                      minDate={getNextValidDate(today)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select booking date"
                      className={`form-control ${errors.booking_date ? 'is-invalid' : ''}`}
                      wrapperClassName="w-100"
                    />
                    {errors.booking_date && <div className="invalid-feedback">{errors.booking_date}</div>}
                    {holidays.length > 0 && (
                      <small className="form-text text-muted">
                        Non-working days are disabled for booking
                      </small>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="booking_time" className="form-label">
                      Booking Time <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-control ${errors.booking_time ? 'is-invalid' : ''}`}
                      id="booking_time"
                      name="booking_time"
                      value={formData.booking_time}
                      onChange={handleChange}
                      disabled={!formData.booking_date || loadingTimeSlots}
                    >
                      <option value="">
                        {loadingTimeSlots 
                          ? 'Loading time slots...' 
                          : !formData.booking_date 
                          ? 'Select date first' 
                          : bookingTimeSlots.length === 0 
                          ? 'No slots available' 
                          : 'Select time'}
                      </option>
                      {bookingTimeSlots.map((slot: any) => (
                        <option key={slot.id} value={slot.id}>
                          {slot.time_slot}
                        </option>
                      ))}
                    </select>
                    {errors.booking_time && <div className="invalid-feedback">{errors.booking_time}</div>}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="peoples" className="form-label">
                    Number of People <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className={`form-control ${errors.peoples ? 'is-invalid' : ''}`}
                    id="peoples"
                    name="peoples"
                    value={formData.peoples}
                    onChange={handleChange}
                    min="1"
                    placeholder="Enter number of people"
                  />
                  {errors.peoples && <div className="invalid-feedback">{errors.peoples}</div>}
                </div>

                <div className="mb-3">
                  <label htmlFor="notes" className="form-label">
                    Notes (Optional)
                  </label>
                  <textarea
                    className="form-control"
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Any special requests or notes..."
                  />
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(routes.home)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableBooking;

